import clsx from 'clsx';

import { Spinner, Tooltip } from '@stern-ui/ui';

import { SkeletonLoader } from '../SkeletonLoader';
import { Typography } from '../Typography';

type CommonProps = {
	className?: string;
	dataTestId?: string;
};

type ButtonCardProps = CommonProps & {
	variant: 'buttonCard';
	title: string;
	subtitle?: string;
	iconSrc: string;

	// not applicable:
	children?: never;
	count?: never;
	isFetching?: never;
	icon?: never;
	backgroundColor?: never;
};

type IconCardProps = CommonProps & {
	variant: 'iconCard';
	title: string;
	titleTooltip?: string;
	showTooltip?: boolean;
	subtitle?: string;
	icon?: React.ReactElement;
	backgroundColor?: string;
	isFetching?: boolean;

	// not applicable:
	children?: never;
	count?: never;
};

type IconNumberCardProps = CommonProps & {
	variant: 'iconNumberCard';
	title: string;
	subtitle?: string;
	icon: string;
	iconNumber: string | number;
	backgroundColor?: string;
	contentClass?: string;

	// not applicable:
	children?: never;
	count?: never;
};

type LayoutCardProps = CommonProps & {
	variant: 'layoutCard';
	children: React.ReactNode;

	// not applicable:
	title?: never;
	subtitle?: never;
	iconSrc?: never;
	count?: never;
	isFetching?: never;
	icon?: never;
	backgroundColor?: never;
};

type TotalsCardProps = CommonProps & {
	variant: 'totalsCard';
	isFetching: boolean;
	subtitle?: string;
	count?: string;
	icon?: React.ReactElement;
	backgroundColor?: string;
	titleTooltip?: string;
	showTooltip?: boolean;
};

export type CardProps =
	| ButtonCardProps
	| IconCardProps
	| IconNumberCardProps
	| LayoutCardProps
	| TotalsCardProps;

export const Card = (props: CardProps) => {
	const sharedCss = 'flex flex-col border-secondaryBorder rounded-[4px]';

	switch (props.variant) {
		case 'buttonCard': {
			const { className, dataTestId, title, subtitle, iconSrc } = props;
			return (
				<div
					className={clsx(
						sharedCss,
						'items-center justify-center h-56 py-16 px-8 border-2 bg-tertiaryBackground',
						className
					)}
					data-test-id={dataTestId}
				>
					<img
						className='w-[48px] group-hover:w-[52px] transition-width duration-300 ease-in-out'
						alt={title}
						aria-hidden='true'
						src={iconSrc}
					/>
					<Typography variant='title2' className='text-primaryTextDark text-center pt-4'>
						{title}
					</Typography>
					{subtitle && (
						<Typography
							variant='body'
							className='text-primaryTextDark text-center pt-2'
						>
							{subtitle}
						</Typography>
					)}
				</div>
			);
		}

		case 'iconCard': {
			const {
				backgroundColor,
				className,
				dataTestId,
				title,
				subtitle,
				icon,
				isFetching = false,
				titleTooltip,
				showTooltip = false,
			} = props;
			return (
				<div
					className={clsx(
						sharedCss,
						'border-1 items-start justify-start p-4 bg-white',
						className
					)}
					data-test-id={dataTestId}
				>
					<span
						className={clsx(
							'w-8 h-8 rounded-full text-center mr-2 relative',
							backgroundColor
						)}
					>
						{icon}
					</span>
					<div className='flex flex-row items-center gap-x-2 mt-4'>
						{isFetching ? (
							<span className='inline-block pt-2 h-[33.5px]'>
								<Spinner variant='light' size='sm' />
							</span>
						) : (
							<Typography
								variant='display4'
								className='text-primaryTextDark text-left break-all'
							>
								{title}
							</Typography>
						)}
						{showTooltip && titleTooltip && !isFetching && (
							<Tooltip
								description={titleTooltip}
								position='right'
								variant='dark'
								size='small'
								tooltipClassName='!w-[225px] !text-left'
							>
								{titleTooltip}
							</Tooltip>
						)}
					</div>
					{subtitle && (
						<Typography variant='body' className='text-tertiaryTextDark text-left pt-2'>
							{subtitle}
						</Typography>
					)}
				</div>
			);
		}

		case 'iconNumberCard': {
			const {
				backgroundColor = 'bg-tertiaryBackground',
				className,
				dataTestId,
				title,
				subtitle,
				icon,
				iconNumber,
				contentClass,
			} = props;
			return (
				<div
					className={clsx(
						sharedCss,
						'rounded-xl items-center justify-center p-4',
						backgroundColor,
						className
					)}
					data-test-id={dataTestId}
				>
					<div
						className={clsx(
							'relative rounded-full text-center relative bg-primaryBrandActive w-16 h-16 flex items-center justify-center'
						)}
					>
						<img src={icon} alt={title} className='w-[24px] h-auto' />
						<span className='absolute top-0 -right-5 bg-white rounded-full border-2 border-primaryBrandFocusOutline w-8 h-8 flex items-center justify-center text-20 text-secondaryTextDark'>
							{iconNumber}
						</span>
					</div>
					<div
						className={clsx(
							'flex flex-row items-center gap-x-2 mt-6 py-2',
							contentClass
						)}
					>
						<Typography
							variant='heading_3_regular'
							className='text-secondaryTextDark text-center text-20!'
						>
							{title}
						</Typography>
					</div>
					{subtitle && (
						<Typography variant='body' className='text-tertiaryTextDark text-left pt-2'>
							{subtitle}
						</Typography>
					)}
				</div>
			);
		}

		case 'layoutCard': {
			const { children, className, dataTestId } = props;
			return (
				<div
					className={clsx('p-6 border-2 border-secondaryBorder rounded-md', className)}
					data-test-id={dataTestId}
				>
					{children}
				</div>
			);
		}

		case 'totalsCard': {
			const {
				isFetching,
				className,
				dataTestId,
				backgroundColor,
				icon,
				subtitle,
				count,
				titleTooltip,
			} = props;

			if (isFetching) {
				return <SkeletonLoader className='h-24 w-full rounded-lg' />;
			}

			return (
				<div
					className={clsx(
						sharedCss,
						'items-left justify-left border-2 p-4 bg-primaryBackground',
						className
					)}
					data-test-id={dataTestId}
				>
					<div className='flex flex-row'>
						<div className='flex flex-col mr-4'>
							<span
								className={clsx(
									'w-8 h-8 rounded-full text-center',
									backgroundColor
								)}
							>
								{icon}
							</span>
						</div>
						<div className='flex flex-col w-full mt-1'>
							<Typography
								variant='body'
								className='text-primaryTextDark text-left mb-2'
							>
								{subtitle}
							</Typography>
							<div className='flex flex-row'>
								<Typography
									variant='bodyXl'
									className='text-primaryTextDark text-left'
								>
									{count}
								</Typography>
								{titleTooltip && !isFetching && (
									<div className='mt-1 ml-3'>
										<Tooltip
											description={titleTooltip}
											position='right'
											variant='dark'
											size='small'
											tooltipClassName='!w-42 !text-left'
										>
											{titleTooltip}
										</Tooltip>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			);
		}

		default:
			return;
	}
};
